import React, { useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Button, Dropdown, Modal, FileUpload } from '../../../../components';
import { ScreenshotTypes } from '../../../../services/scraper';

type UploadScreenshotModalProps = {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, screenshotType: ScreenshotTypes) => void;
  uploading?: boolean;
};

const screenshotTypeOptions = [
  { value: ScreenshotTypes.DESKTOP, label: 'Desktop' },
  { value: ScreenshotTypes.MOBILE, label: 'Mobile' },
];

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/x-png',
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const UploadScreenshotModal: React.FC<UploadScreenshotModalProps> = ({
  open,
  onClose,
  onUpload,
  uploading = false,
}) => {
  const [selectedScreenshotType, setSelectedScreenshotType] = useState<string>(
    ScreenshotTypes.DESKTOP
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (file: File) => {
    setSelectedFile(file);
    setError(null);
  };

  const handleValidationError = (errorMessage: string) => {
    setError(errorMessage);
    setSelectedFile(null);
  };

  const handleConfirmUpload = () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    onUpload(selectedFile, selectedScreenshotType as ScreenshotTypes);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedScreenshotType(ScreenshotTypes.DESKTOP);
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="small"
      title="Upload Screenshot"
      contentClassName="p-6"
    >
      <div className="flex flex-col gap-6">
        <div>
          <label className="block mb-2 text-sm font-medium text-mainText">
            Screenshot Type:
          </label>
          <Dropdown
            options={screenshotTypeOptions}
            value={selectedScreenshotType}
            onChange={value =>
              setSelectedScreenshotType(value as ScreenshotTypes)
            }
            placeholder="Select screenshot type"
          />
        </div>

        <div>
          <FileUpload
            onChange={handleFileChange}
            onValidationError={handleValidationError}
            accept=".jpg,.jpeg,.png,image/png,image/x-png,image/jpeg,image/jpg,image/pjpeg"
            allowedFileTypes={ALLOWED_FILE_TYPES}
            maxSize={MAX_FILE_SIZE}
            showFileSize
            loading={uploading}
            label="Select Image File (JPEG/JPG/PNG):"
            labelClassName="block mb-2 text-sm font-medium text-mainText"
            className="items-start"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-3">
          <Button
            label="Cancel"
            onClick={handleClose}
            type="contrast"
            size="small"
            className="px-6"
          />
          <Button
            label="Upload"
            onClick={handleConfirmUpload}
            size="small"
            className="px-6 gap-3"
            icon={<CloudUploadIcon className="h-4 w-4" />}
            disabled={uploading || !selectedFile}
          />
        </div>
      </div>
    </Modal>
  );
};
